const useState = React.useState
const useMemo = React.useMemo
const useCallback = React.useCallback
const useEffect = React.useEffect

interface Props<T> {

    containerClassName: string

    items: T[]
    getRowHeight(item: T, index: number): number
    render(item: T, height: number): React.ReactNode

    getInitScrollPosition?: (rect: DOMRectReadOnly) => {
        left: number,
        top: number
    }

    setScrollPosition?: (position: { left: number, top: number }, clientRect: DOMRectReadOnly) => void

}

export function VirtualList<T>({ render, containerClassName, items, getRowHeight, getInitScrollPosition, setScrollPosition }: Props<T>) {

    const [scroll, setScroll] = useState({ top: 0, left: 0, set: false })

    const { ref, node, rect: clientRect } = useClientRect()

    const { rows, totalHeight } = useMemo(() => getRows({ items, getRowHeight }), [items, getRowHeight])

    const onScroll = (event: React.UIEvent<HTMLDivElement>) => {

        const position = {
            top: event.currentTarget.scrollTop,
            left: event.currentTarget.scrollLeft,
        }

        setScroll({
            ...position,
            set: true
        })

        if (setScrollPosition) {
            setScrollPosition(position, clientRect)
        }

    }

    useEffect(() => {

        if (getInitScrollPosition && node && (!scroll.set) && clientRect?.width) {

            const p = getInitScrollPosition(clientRect)

            if (p) {
                node.scroll({ ...p })
                setScroll(s => ({ ...s, ...p, set: true }))
            }
        }

    }, [getInitScrollPosition, node, clientRect?.width, clientRect?.height, scroll.set, rows])


    const containerHeight = Math.ceil(clientRect?.height ?? 0)

    const window = useMemo(
        () => getWindow(containerHeight, scroll.top, rows, totalHeight),
        [containerHeight, scroll.top, rows, totalHeight]
    )

    return <div ref={ref} className={containerClassName} onScroll={e => onScroll(e)} style={{ height: "100%", overflow: "auto" }}>

        <div style={{ height: window.topMargin }}></div>

        {/* Note: should probably use a reliable id rather than r.index */}
        {window.rows.map(r => <div key={r.index} style={{ height: r.height }}>
            {render(r.item, r.height)}
        </div>)}

        <div style={{ height: window.bottomMargin }}> </div>

    </div>

}


interface Row<T> {
    top: number
    height: number
    bottom: number
    index: number
    item: T
}

type Rows<T> = Row<T>[]

function getRows<T>({ items, getRowHeight }: Omit<Props<T>, "render" | "containerClassName">) {

    const rows: Row<T>[] = []
    let top = 0

    for (let index = 0; index < items.length; index++) {

        const item = items[index]
        const height = getRowHeight(item, index)

        rows.push({
            top,
            height,
            bottom: top + height,
            index,
            item
        })

        top += height

    }

    return { rows, totalHeight: top }

}

function getWindow<T>(containerHeight: number, scrollTop: number, rows: Rows<T>, totalHeight: number) {

    const bottom = scrollTop + containerHeight
    let firstRowIndex = -1
    let lastRowIndex = -1
    let topMargin = 0

    for (let index = 0; index < rows.length; index++) {

        const row = rows[index]

        if ((row.top >= scrollTop) || (row.bottom >= scrollTop)) {
            firstRowIndex = index
            break
        }

        topMargin += row.height

    }

    if (firstRowIndex >= 0) {

        for (let index = firstRowIndex; index < rows.length; index++) {

            lastRowIndex = index

            const row = rows[index]

            if (row.bottom >= bottom) {
                break
            }

        }

    }

    const window = firstRowIndex >= 0
        ? rows.slice(firstRowIndex, lastRowIndex + 1)
        : []

    const height = window.reduce((a, b) => a + b.height, 0)

    return {
        firstRowIndex,
        lastRowIndex,
        topMargin,
        bottomMargin: totalHeight - (topMargin + height),
        rows: window
    }

}

function useClientRect() {

    const [state, setState] = useState(() => ({
        node: undefined as unknown as HTMLDivElement,
        rect: undefined as unknown as DOMRectReadOnly
    }))

    const ref = useCallback((node: HTMLDivElement) => {

        const rect = node?.getBoundingClientRect()
        setState({ node, rect })

    }, []);

    useEffect(() => {

        if (state.node) {

            const obs = new ResizeObserver(entries => {
                const rect = entries[0].contentRect
                setState({ node: state.node, rect })
            })

            obs.observe(state.node)

            return () => obs.disconnect()

        }

    }, [state.node])

    return {
        ref,
        ...state
    }

}
